import React, { useState } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Dimensions,
} from "react-native";

// Define __DEV__ if it doesn't exist (for web environments)
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { createApiClient, apiClient,getLocalTimestamp } from "../../api/apiClient";
import { handleShiftHandoverErrorWithFriendlyMessage, SHIFTHANDOVER_API_NAMES } from "../../store/utils/shiftHandoverErrorHandler";
import {
  setSuccessMessage,
  resetShiftHandoverState,
} from "../../store/slices/shiftHandOverSlice";
import { useToast } from "../../components/LhToastContainer";
import { useTheme } from "../../theme";

interface ShiftHandOverActionProps {
  onComplete: (action: "save" | "complete") => boolean; // Return boolean for validation result
  onBackToDashboard?: () => void;
  onShowMessage?: (type: "success" | "error", message: string) => void;
  selectedHandover?: any; // Add selectedHandover as an optional prop
  forceDisabled?: boolean; // NEW: force disabled state
}

const ShiftHandOverAction: React.FC<ShiftHandOverActionProps> = ({
  onComplete,
  onBackToDashboard,
  onShowMessage,
  selectedHandover, // Use the prop, not Redux
  forceDisabled = false, // default to false
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast(); // Get data from Redux state
  const {
    selectedShift,
    selectedAttendees,
    handoverState,
    selectedQuestions,
    observationNotes,
    miscObservationImages,
    shiftHandoverImages,
    signatureFileName,
    isSignatureUpdated,
  } = useSelector((state: RootState) => state.shiftHandOver);

  // Get location from appData state
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  const period = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.period
  );

  const week = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.week
  );

  // Get user information from Redux auth state
  const userEmail = useSelector(
    (state: RootState) => state.auth.user?.username
  );

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    if (isPdfExportDisabled || !userEmail) {
      return;
    }

    try {
      setIsPdfExporting(true);

      // Build the subject line
      const recordName = selectedShift?.displayText || "Unknown";
      // Use selectedHandover?.ID for the id in the API call
      const handoverId = selectedHandover?.ID || "";
      const subjectLine = `Shift Handover Report for ${recordName} - ${handoverId}`;

      // Build the API URL with query parameters
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/notifications/email/pdf?emailTo=${encodeURIComponent(
          userEmail
        )}&inspection_module=shift_handover_walk&id=${handoverId}&subject=${encodeURIComponent(
          subjectLine
        )}`,
        "GET"
      );

      // Handle the response
      if (response && response.message) {
        toast.showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
      } else {
        toast.showToast({
          header: "Email Error",
          subtext: "Unknown error occurred while sending email",
          variant: "error",
        });
      }
    } catch (error: any) {
      console.error("PDF Export Error:", error);

      // Show error toast notification
      toast.showToast({
        header: "Email Error",
        subtext: error?.message || "Failed to send PDF export email",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: string) => {
    try {
      // First call the validation function provided by the parent component
      const isValid = onComplete(action === "save" ? "save" : "complete");

      // If validation failed, exit early
      if (!isValid) {
        return;
      }

      // Set submitting state to true to disable buttons
      setIsSubmitting(true);

      // Use email for createdBy/modifiedBy in payload (for backend)
      const userEmailForPayload = userEmail || "";

      // Define the payload type
      interface ShiftHandOverPayload {
        oneSubmission?: boolean;
        selectedRecord?: {
          id: number;
        };
        walkData: {
          location: string;
          walkType: string;
          period?: number;
          week?: number;
          shift: string;
          miscellImageDesc?: string;
          miscellImage2Desc?: string;
          managerSignature: string;
          miscellImage?: string;
          miscellImage2?: string;
          updatedImg1?: boolean;
          updatedImg2?: boolean;
          updatedManagerSign?: boolean;
          attendees: string;
          status: string;
          notes: string;
          createdBy: string;
        };
        toolsMapping: {
          toolId: number;
        }[];
        shiftHandoverImages?: {
          ImageId: string;
          ImageBase64: string;
          Description: string;
          ObservationId: string;
        }[];
        // Additional fields for tracking creation/modification
        created?: string;
        createdBy?: string;
        modified?: string;
        modifiedBy?: string;
      }

      // Format attendees as a comma-separated string of display names
      const attendeesString = selectedAttendees
        .map((attendee) => attendee.userPrincipalName)
        .join(", ");

      // Prepare the API request payload (send emails for createdBy/modifiedBy)
      const payload: ShiftHandOverPayload = {
        walkData: {
          location:
            selectedLocation?.name || selectedLocation?.displayText || "",
          walkType: "Shift Handover", // Always "Shift Handover"
          period: period ? parseInt(String(period), 10) : undefined,
          week: week ? parseInt(String(week), 10) : undefined,
          shift: selectedShift?.id || "",

          // Miscellaneous Observations
          miscellImageDesc: miscObservationImages[0]?.description || "",
          miscellImage2Desc: miscObservationImages[1]?.description || "",
          miscellImage: miscObservationImages[0]?.fileName || "",
          miscellImage2: miscObservationImages[1]?.fileName || "",
          updatedImg1: Boolean(miscObservationImages[0]?.fileName),
          updatedImg2: Boolean(miscObservationImages[1]?.fileName), // Manager signature - includes the filename from the signature pad
          managerSignature: signatureFileName || "",
          // Only set updatedManagerSign to true if the signature was newly uploaded
          // This uses the isSignatureUpdated flag which is only true for new uploads
          updatedManagerSign: isSignatureUpdated,
          // Attendees, status, notes
          attendees: attendeesString,
          status: action === "save" ? "In Progress" : "Completed",
          notes: observationNotes,
          // Creator information
          createdBy: userEmailForPayload,
        },
        // Map selected questions to tool IDs
        toolsMapping: selectedQuestions.map((question) => ({
          toolId: parseInt(question.id, 10) || 0,
        })),
      };

      // If the handoverState is "new", set createdBy and created fields (as email)
      if (handoverState === "new") {
        payload.oneSubmission = false;
        payload.created = getLocalTimestamp()
        payload.createdBy = userEmailForPayload;

        // Only set modifiedBy if not saving as "In Progress"
        if (action !== "save") {
          payload.modifiedBy = userEmailForPayload;
          payload.modified = getLocalTimestamp()
        }
      }
      // If the handoverState is "in-progress", add oneSubmission and selectedRecord (as email)
      if (handoverState === "in-progress") {
        // Only set modified information if not saving as "In Progress"
        if (action !== "save") {
          payload.modifiedBy = userEmailForPayload;
          payload.modified = getLocalTimestamp()
        }

        // Add submission details
        payload.oneSubmission = true;
        payload.selectedRecord = {
          id: selectedHandover?.ID || 0, // Use actual record ID if available
        };
      }
      if (shiftHandoverImages && shiftHandoverImages.length > 0) {
        payload.shiftHandoverImages = shiftHandoverImages.map((img) => {
          // Use the imageId directly from the Redux store
          // This preserves the original ImageId from the API response
          const imageId = img.imageId;

          return {
            ImageId: imageId,
            ImageBase64: img.imageBase64,
            Description: img.description,
            ObservationId: img.questionId,
          };
        });
      } // Log payload for debugging in development
      if (__DEV__) {
        console.log("ShiftHandOver Submit Payload:", payload);
        // Log shiftHandoverImages data for debugging
        if (shiftHandoverImages && shiftHandoverImages.length > 0) {
          console.log(
            "ShiftHandoverImages data being sent:",
            shiftHandoverImages.map((img) => ({
              imageId: img.imageId,
              description: img.description,
              hasImageBase64: Boolean(img.imageBase64),
              observationId: img.questionId,
            }))
          );
        }
      }

      // Make the API call
      const api = createApiClient();

      // Clone payload for logging without large image data
      const payloadForLogging = JSON.parse(JSON.stringify(payload));
      // Remove large binary data for more readable logs
      if (payloadForLogging.shiftHandoverImages) {
        payloadForLogging.shiftHandoverImages =
          payloadForLogging.shiftHandoverImages.map((img: any) => ({
            ...img,
            ImageBase64: img.ImageBase64
              ? `[Binary data of length ${img.ImageBase64.length}]`
              : null,
          }));
      }
      console.log(
        "DEBUG: API Payload (walkData only):",
        JSON.stringify(payloadForLogging.walkData, null, 2)
      );

      const response = await apiClient(
        api,
        "/shift-handover-walks/submit",
        "POST",
        payload
      );

      // Handle the API response
      // Status Code 201: Created successfully with walkId
      // Status Code 200: Updated successfully
      // Status Code 400: Bad Request
      // Status Code 500: Server Error
      if (response) {
        // Check if we have a walkId (new record - status 201) or an update (status 200)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const walkId = response.walkId || payload.selectedRecord?.id || 0;
        let successMsg;

        if (response.walkId) {
          // New record created successfully (Status Code 201)
          successMsg =
            action === "save"
              ? "Shift Handover saved successfully."
              : "Shift Handover completed successfully.";
        } else {
          // Record updated successfully (Status Code 200)
          successMsg =
            action === "save"
              ? "Shift Handover updated successfully."
              : "Shift Handover completed successfully.";
        }

        // No need to Update state handOverState based on action type

        // Set success message in Redux for ShiftHandOver screen to display
        dispatch(setSuccessMessage(successMsg));

        // Add a small delay before resetting state to ensure success message is processed
        setTimeout(() => {
          // Reset the ShiftHandOver state except for the preserved values
          dispatch(resetShiftHandoverState());
          console.log(
            "API call successful, state reset, navigating back to ShiftHandOver screen"
          );
          // Always redirect after submit
          if (onBackToDashboard) {
            onBackToDashboard();
          }
        }, 100); // small delay to ensure Redux updates properly
      } else {
        // Handle error responses based on status code
        let errorTitle = "Error";
        let errorMessage = "Failed to submit shift handover";

        // Check for different status codes or response formats
        if (response?.status === 400) {
          // Bad Request (400)
          errorTitle = "Validation Error";
          errorMessage =
            response.message ||
            "Invalid data submitted. Please check your entries.";

          // Format validation details if they exist
          if (response.details) {
            const detailsStr = Object.entries(response.details || {})
              .map(([field, msg]) => `${field}: ${msg}`)
              .join("\n");

            if (detailsStr) {
              errorMessage = `${errorMessage}\n${detailsStr}`;
            }
          }
        } else if (response?.status === 500) {
          // Server Error (500)
          errorTitle = "Server Error";
          errorMessage =
            response.message ||
            "A server error occurred. Please try again later.";
        } else if (response?.status === "error") {
          // Handle legacy error format
          errorMessage = response.message || errorMessage;

          if (response.message === "Validation failed" && response.details) {
            errorTitle = "Validation Error";
            const detailsStr = Object.entries(response.details || {})
              .map(([field, msg]) => `${field}: ${msg}`)
              .join("\n");

            if (detailsStr) {
              errorMessage = `${errorMessage}\n${detailsStr}`;
            }
          }
        } else if (response?.message) {
          // Generic error with message
          errorMessage = response.message;
        }

        // Show error message if onShowMessage is available
        if (onShowMessage) {
          onShowMessage("error", errorMessage);
        } else {
          // Show error toast
          toast.showToast({
            header: errorTitle,
            subtext: errorMessage,
            variant: "error",
          });
        }

        // Also show centralized overlay (friendly message) for submit failures
        try {
          const overlayMsg = "Unable to submit shift handover. Please try again.";
          handleShiftHandoverErrorWithFriendlyMessage(
            SHIFTHANDOVER_API_NAMES.SUBMIT_HANDOVER,
            overlayMsg,
            true,
            { response }
          );
        } catch (e) {
          console.error('Failed to show centralized error overlay for submit:', e);
        }
      }
    } catch (error: any) {
      console.error("API Error:", error);

      // Parse the error response based on HTTP status codes
      let errorTitle = "Error";
      let errorMessage =
        "An error occurred while submitting the shift handover";

      if (error?.response) {
        // Handle different HTTP status codes
        const statusCode = error.response.status;

        if (statusCode === 400) {
          // Bad Request (Validation Error)
          errorTitle = "Validation Error";
          errorMessage =
            error.response.data?.message ||
            "The submitted data was invalid. Please check your entries.";

          // Format validation details if they exist
          if (error.response.data?.details) {
            const detailsStr = Object.entries(error.response.data.details)
              .map(([field, msg]) => `${field}: ${msg}`)
              .join("\n");

            if (detailsStr) {
              errorMessage = `${errorMessage}\n${detailsStr}`;
            }
          }
        } else if (statusCode === 500) {
          // Server Error
          errorTitle = "Server Error";
          errorMessage =
            error.response.data?.message ||
            "A server error occurred. Please try again later.";
        } else {
          // Other error types
          errorMessage =
            error.response.data?.message || error.message || errorMessage;
        }
      } else if (error?.message) {
        // Network error or other error with message
        errorMessage = error.message;
      }

      // Show error message
      if (onShowMessage) {
        onShowMessage("error", errorMessage);
      } else {
        toast.showToast({
          header: errorTitle,
          subtext: errorMessage,
          variant: "error",
        });
      }

      // Show centralized overlay for caught exceptions as well
      try {
        const overlayMsg = "Unable to submit shift handover. Please try again.";
        handleShiftHandoverErrorWithFriendlyMessage(
          SHIFTHANDOVER_API_NAMES.SUBMIT_HANDOVER,
          overlayMsg,
          true,
          { originalError: error }
        );
      } catch (e) {
        console.error('Failed to show centralized error overlay in submit catch:', e);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the PDF export box should be disabled (when not completed yet)
  const isPdfExportDisabled =
    forceDisabled || handoverState === "new" || handoverState === "in-progress";

  const window = Dimensions.get('window');
  const isIPad = Platform.OS === 'ios' && window.width >= 768;
  const isTabletOrWeb = Platform.OS === 'web' || isIPad;

  return (
    <View style={styles.actionRow}>
      {/* PDF Export Button */}
      <TouchableOpacity
        style={[
          isPdfExportDisabled ? styles.disabledButton : styles.pdfExportButton,
          isDarkMode && !isPdfExportDisabled && { backgroundColor: colors.brand.primary },
          isDarkMode && isPdfExportDisabled && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
          { width: isTabletOrWeb ? 189 : '100%', marginBottom: isTabletOrWeb ? 0 : 10 },
          Platform.OS === 'web' && !isPdfExportDisabled ? { cursor: 'pointer' as any } : {},
        ]}
        onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
        disabled={isPdfExportDisabled}
        accessibilityState={{ disabled: isPdfExportDisabled }}
        accessibilityLabel={
          isPdfExportDisabled
            ? 'PDF Export Not Available'
            : isPdfExporting
            ? 'Sending PDF export email'
            : 'PDF Export'
        }
      >
        <View style={styles.buttonContent}>
          {isPdfExportDisabled ? (
            <>
              <MaterialIcons name="picture-as-pdf" size={24} color="#B1B1B1" style={{ marginRight: 8 }} />
              <Text style={[styles.buttonTextDisabled, isDarkMode && { color: colors.text.disabled }]}>PDF Export</Text>
            </>
          ) : isPdfExporting ? (
            <>
              <ActivityIndicator size="small" color="#FFF" style={{ marginRight: 8 }} />
              <Text style={[styles.buttonText, isDarkMode && { color: colors.text.inverse }]}>Sending Email...</Text>
            </>
          ) : (
            <>
              <MaterialIcons name="picture-as-pdf" size={24} color={colors.text.inverse} style={{ marginRight: 8 }} />
              <Text style={[styles.buttonText, isDarkMode && { color: colors.text.inverse }]}>PDF Export</Text>
            </>
          )}
        </View>
      </TouchableOpacity>
      {/* Save for Later Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.saveButton,
          isDarkMode && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
          { width: isTabletOrWeb ? 189 : '100%', marginBottom: isTabletOrWeb ? 0 : 10 },
          isSubmitting || handoverState === 'completed' || forceDisabled ? styles.disabledButton : {},
          Platform.OS === 'web' && !(isSubmitting || handoverState === 'completed' || forceDisabled) ? { cursor: 'pointer' as any } : {},
        ]}
        onPress={() => handleSubmit('save')}
        disabled={isSubmitting || handoverState === 'completed' || forceDisabled}
        accessibilityState={{ disabled: isSubmitting || handoverState === 'completed' || forceDisabled }}
        accessibilityLabel={
          isSubmitting
            ? 'Save for later (disabled during submission)'
            : handoverState === 'completed'
            ? 'Save for later (disabled for completed handover)'
            : 'Save for later'
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator size="small" color="#034895" style={{ marginRight: 8 }} />
          ) : (
            <>
              <MaterialIcons
                name="save"
                size={24}
                color={
                  isSubmitting || handoverState === 'completed' || forceDisabled
                    ? '#B1B1B1'
                    : isDarkMode
                    ? colors.text.primary
                    : '#034895'
                }
                style={{ marginRight: 8 }}
              />
              <Text
                style={
                  isSubmitting || handoverState === 'completed' || forceDisabled
                    ? [styles.buttonTextDisabled, isDarkMode && { color: colors.text.disabled }]
                    : [styles.saveButtonText, isDarkMode && { color: colors.text.primary }]
                }
              >
                Save for Later
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>
      {/* Complete Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.completeButton,
          isDarkMode && { backgroundColor: colors.brand.primary },
          { width: isTabletOrWeb ? 189 : '100%', marginBottom: isTabletOrWeb ? 0 : 10 },
          isSubmitting || handoverState === 'completed' || forceDisabled ? styles.disabledButton : {},
          Platform.OS === 'web' && !(isSubmitting || handoverState === 'completed' || forceDisabled) ? { cursor: 'pointer' as any } : {},
        ]}
        onPress={() => handleSubmit('complete')}
        disabled={isSubmitting || handoverState === 'completed' || forceDisabled}
        accessibilityState={{ disabled: isSubmitting || handoverState === 'completed' || forceDisabled }}
        accessibilityLabel={
          isSubmitting
            ? 'Complete form (disabled during submission)'
            : handoverState === 'completed'
            ? 'Complete form (disabled for completed handover)'
            : 'Complete form'
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator size="small" color="#B1B1B1" style={{ marginRight: 8 }} />
          ) : (
            <Text
              style={
                isSubmitting || handoverState === 'completed' || forceDisabled
                  ? [styles.buttonTextDisabled, isDarkMode && { color: colors.text.disabled }]
                  : [styles.buttonText, isDarkMode && { color: colors.text.inverse }]
              }
            >
              Complete
            </Text>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === 'web' || (Platform.OS === 'ios' && Dimensions.get('window').width >= 768) ? 'row' : 'column',
    flexWrap: 'wrap',
    marginTop: 24,
    paddingBottom: 16,
    alignItems: Platform.OS === 'web' || (Platform.OS === 'ios' && Dimensions.get('window').width >= 768) ? 'flex-start' : 'stretch',
    justifyContent: Platform.OS === 'web' || (Platform.OS === 'ios' && Dimensions.get('window').width >= 768) ? 'flex-start' : 'center',
    width: '100%',
    gap: Platform.OS === 'web' || (Platform.OS === 'ios' && Dimensions.get('window').width >= 768) ? 20 : 8,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: 8,
  },
  customButton: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: '#034895',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 120,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#034895',
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completeButton: {
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfExportButton: {
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#DADADA',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    shadowOpacity: 0.1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'not-allowed' as any } : {}),
  },
  buttonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0,
  },
  buttonTextDisabled: {
    color: '#B1B1B1',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 22,
    letterSpacing: 0,
  },
  saveButtonText: {
    color: '#034895',
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0,
  },
});

export default ShiftHandOverAction;
